import React, { CSSProperties, SyntheticEvent, useEffect, useMemo, useState } from "react";

import { Button, ButtonProps, FormField, FormGroup, Input, InputProps } from "semantic-ui-react";

import moment from "moment";
import { useIntl } from "react-intl";
import { ComponentPropsGetterR } from "react-table-6";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import DropdownOptions from "react-lib/appcon/common/DropdownOptions";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";

import ModConfirmCNMI from "react-lib/apps/common/cnmi/ModConfirm";
import ModInfo from "react-lib/apps/common/ModInfo";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import TimeComboBox from "react-lib/apps/common/TimeComboBox";

import { getRequireKeyError } from "react-lib/apps/USER/CardSettingEmployee";

import CardCourseListUX from "./CardCourseListUX";
import SubLinkAttachment from "./SubLinkAttachment";

import {
  ACTIONS,
  ActionSaveType,
  BTN_ACTS,
  OnEvent,
  PickedState,
  RequiredFieldType,
  RunSequence,
  SEQUENCE_NAME,
  SetProp,
  State,
} from "./sequence/CourseList";

import { COURSE_STATUS_OPTIONS, COURSE_TYPE_OPTIONS, getCourseStatusColor } from "./HRMInterface";

import { beStringToMoment, formatDate, formatDatetime } from "react-lib/utils/dateUtils";

// Types
type CardCourseListProps = Pick<
  PickedState,
  "buttonLoadCheck" | "django" | "errorMessage" | "searchedItemListWithKey"
> & {
  onEvent: OnEvent;
  setProp: SetProp;
  runSequence: RunSequence;
  CourseListSequence: State["CourseListSequence"];
  style?: CSSProperties;
  hideDeleteCourse?: boolean;
  hideNewCourse?: boolean;
  readOnly?: boolean;
  languageUX: "en" | "th";
  divisionOptions: Record<string, any>[];
};

const IMAGES = {
  cautionSign: "/static/images/time-attendance/caution-sign.png",
};

const CardCourseList = (props: CardCourseListProps) => {
  const intl = useIntl();

  const [modConfirm, setModConfirm] = useState<{ canceled?: boolean } | null>(null);
  const [modConfirmChange, setModConfirmChange] = useState<{
    isByPass: boolean;
    type: ActionSaveType;
  } | null>(null);

  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [link, setLink] = useState<string>("");

  const [showRequiredField, setShowRequiredField] = useState<RequiredFieldType>({});

  useEffect(() => {
    props.runSequence({ sequence: "CourseList", restart: true });
  }, []);

  const detail = useMemo(
    () => props.CourseListSequence?.detail,
    [props.CourseListSequence?.detail]
  );

  const filteredAttachments = useMemo(
    () =>
      (detail?.links ?? [])
        .map((item, index) => ({ ...item, index }))
        .filter((item) => !item.inactive),
    [detail?.links]
  );

  const filteredEvaluations = useMemo(
    () =>
      (detail?.evaluation ?? [])
        .map((item, index) => ({ ...item, index }))
        .filter((item) => !item.inactive),
    [detail?.evaluation]
  );

  const filteredParticipants = useMemo(
    () =>
      (detail?.participants ?? [])
        .map((item, index) => ({ ...item, index }))
        .filter((item) => !item.inactive),
    [detail?.participants]
  );

  const modConfirmTitle = useMemo(() => {
    if (modConfirm?.canceled) {
      return "Would you like to send cancellation emails to ALL participants ?";
    }

    // บันทึกข้อมูลครั้งแรก
    const title = "Would you like to send invitation emails to participants ?";

    if (!detail?.id) {
      return title;
    }

    const participants = detail.participants ?? [];
    const hasNew = participants.some((item) => !item.id && !item.inactive);
    const hasRemove = participants.some((item) => item.id && item.inactive);

    if (!detail.is_editing) {
      if (hasRemove && hasNew) {
        // มีการแก้ไข เฉพาะการเพิ่ม/ลด Participant
        return "Would you like to send invitation emails to UPDATED participants?";
      } else if (hasNew) {
        // มีการเพิ่ม Participant
        return "Would you like to send invitation emails to NEW participants ?";
      } else if (hasRemove) {
        // มีการแก้ไข Course โดยการลบผู้เข้าร่วมบางส่วน
        return "Would you like to send cancellation emails to REMOVED participants ?";
      }
    }

    if (detail.is_editing && !hasRemove && !hasNew) {
      // มีการแก้ไขรายละเอียดเฉพาะ Course
      return "Would you like to send updated emails to participants?";
    }

    // มีการแก้ไขทั้งรายละเอียด Course และ เพิ่ม/ลด Participant
    return "Would you like to send updated emails to participants?";
  }, [detail?.participants, modConfirm]);

  const formattedItems = useMemo(
    () =>
      (props.CourseListSequence?.pagination?.items ?? []).map((item) => {
        const typeName = COURSE_TYPE_OPTIONS.find((option) => option.value === item.training_type);
        const statusName = COURSE_STATUS_OPTIONS.find((option) => option.value === item.status);

        return {
          ...item,
          created: (
            <div style={{ color: "rgba(0,0,0,.87)", textAlign: "left" }}>
              <div>{item.create_user_name}</div>
              <div>{formatDatetime(item.created, true)}</div>
            </div>
          ),
          expiryDate: formatDate(moment(item.expiry_date)),
          startDate: formatDate(moment(item.start_date)),
          statusName: (
            <div style={{ textAlign: "left" }}>
              <div>{statusName?.text || ""}</div>
              {!item.is_active && (
                <div style={{ color: "red", fontSize: "0.85em" }}>(INACTIVE)</div>
              )}
            </div>
          ),
          title: <div style={{ textAlign: "left" }}>{item.subject}</div>,
          typeName: typeName?.text || "",
        };
      }),
    [props.CourseListSequence?.pagination?.items]
  );

  const isRoleHR = useMemo(() => {
    const roleTypes: string[] = props.django?.user?.role_types || [];

    return roleTypes.includes("HR");
  }, [props.django]);

  const enabledExpiryDate = useMemo(() => {
    const status = props.CourseListSequence?.detail?.status || "";

    // กรณีเป็น role type อื่น จะแก้ไข Expiry date ได้แค่ status OPEN
    return isRoleHR ? true : ["OPEN"].includes(status);
  }, [isRoleHR, props.CourseListSequence?.detail]);

  const enabledParticipant = useMemo(() => {
    const expiryDateStr = props.CourseListSequence?.detail?.expiry_date;

    if (!expiryDateStr) {
      return true;
    }

    const expiryDate: moment.Moment = /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(expiryDateStr)
      ? beStringToMoment(expiryDateStr)
      : moment.parseZone(expiryDateStr);

    if (!expiryDate.isValid()) {
      console.warn("Invalid expiry date", expiryDateStr);

      return false;
    }

    const currentDate = moment();
    const canEnabled = expiryDate.isSameOrAfter(currentDate, "day"); // compare only dates

    console.log(
      "Enable participant:",
      canEnabled,
      expiryDate.format("YYYY-MM-DD"),
      currentDate.format("YYYY-MM-DD")
    );

    return canEnabled;
  }, [props.CourseListSequence?.detail?.expiry_date, props.django]);

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;

    props.setProp("CourseListSequence.filter", {
      ...props.CourseListSequence?.filter,
      [name]: data.value,
    });
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "CourseList",
      action: ACTIONS.SEARCH,
      activePage: props.CourseListSequence?.pagination?.activePage,
    });
  };

  const handleGetTrProps: ComponentPropsGetterR = (state, rowInfo) => {
    const { original } = rowInfo ?? {};

    return {
      className: detail?.id && detail.id === original?.id ? "blueSelectedRow" : "",
      style: {
        color: getCourseStatusColor(original?.status),
        textAlign: "center",
      },
      onClick: () => {
        setShowRequiredField({});

        props.runSequence({ sequence: "CourseList", action: ACTIONS.SELECT, id: original?.id });
      },
    };
  };

  const handleClickNewCourse = () => {
    setShowRequiredField({});

    props.setProp("CourseListSequence.detail", {});
  };

  const handleChangeValue = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name: string = data.name;
    const value = data.type === "checkbox" ? data.checked : data.value;
    const updatedDetail: any = { ...detail, is_editing: true };

    if (["course_period", "pricing"].includes(name)) {
      if (/^\d*$/.test(value)) {
        updatedDetail[name] = value;
      }
    } else if (name === "participant") {
      updatedDetail[name] = value;

      if (value === "department") {
        setSelectedEmployee(null);
      } else {
        updatedDetail["division"] = null;
      }
    } else {
      updatedDetail[name] = value;
    }

    props.setProp("CourseListSequence.detail", { ...updatedDetail });
  };

  const handleAddEvaluation = () => {
    const evaluation = detail?.evaluation ?? [];

    const hasEmpty = evaluation.some((item) => {
      const isActive = !item.inactive;
      const isEmpty = !item.link && !item.subject;

      return isActive && isEmpty;
    });

    if (hasEmpty) {
      return;
    }

    evaluation.push({ link: "", subject: "" });

    props.setProp("CourseListSequence.detail.evaluation", [...evaluation]);
  };

  const handleChangeEvaluation = (index: number) => (e: SyntheticEvent, data: InputProps) => {
    const evaluation: any[] = detail?.evaluation ?? [];

    evaluation[index][data.name] = data.value;

    props.setProp("CourseListSequence.detail.evaluation", [...evaluation]);
  };

  const handleDeleteEvaluation = (index: number) => () => {
    const evaluation = detail?.evaluation ?? [];

    evaluation[index].inactive = true;

    props.setProp("CourseListSequence.detail.evaluation", [...evaluation]);
  };

  const handleChangeEmployee = (index: number) => (e: SyntheticEvent, data: InputProps) => {
    const participants: any[] = detail?.participants ?? [];

    participants[index][data.name] = data.value;

    props.setProp("CourseListSequence.detail.participants", [...participants]);
  };

  const handleDeleteEmployee = (index: number) => () => {
    const participants = detail?.participants ?? [];

    participants[index].inactive = true;
    participants[index].active = false;

    props.setProp("CourseListSequence.detail.participants", [...participants]);
  };

  const handleAddEmployee = () => {
    if (props.CourseListSequence?.detail?.participant === "employee") {
      const participants = detail?.participants ?? [];

      participants.push({
        ...selectedEmployee,
        id: null,
        employee: selectedEmployee.id,
        employee_name: selectedEmployee?.full_name,
      });

      const uniqueParticipants = participants.filter(
        (participant, index, self) =>
          index === self.findIndex((p) => p.employee === participant.employee)
      );

      props.setProp("CourseListSequence.detail.participants", [...uniqueParticipants]);

      setSelectedEmployee(null);

      return;
    }

    props.runSequence({ sequence: "CourseList", action: ACTIONS.ADD_DIVISION });
  };

  const handleDeleteLink = (index: number) => () => {
    const links = detail?.links ?? [];

    links[index].inactive = true;

    props.setProp("CourseListSequence.detail.links", [...links]);
  };

  const handleChangeLink = (e: SyntheticEvent, data: InputProps) => {
    setLink(data.value);
  };

  const handleAddLink = () => {
    const links = detail?.links ?? [];

    links.push({ link: link.trim(), title: "" });

    props.setProp("CourseListSequence.detail.links", [...links]);

    setLink("");
  };

  const handleSave = () => {
    const requiredKey = {
      course_period: "Course period",
      end_date: "กรุณาระบุ End date",
      end_time: "กรุณาระบุ Start time",
      expiry_date: "กรุณาระบุ Expiry date",
      pricing: "กรุณาระบุ Pricing",
      start_date: "กรุณาระบุ Start date",
      start_time: "กรุณาระบุ Start time",
      subject: "กรุณาระบุ Title ",
      training_type: "กรุณาระบุ Type",
    };

    const error = getRequireKeyError(requiredKey as any, { ...detail }, intl);

    setShowRequiredField(error);

    if (Object.keys(error).length === 0) {
      setModConfirm({});
    }
  };

  const handleCloseModConfirm = () => {
    setModConfirm(null);
    setModConfirmChange(null);
  };

  const handleToggleActive = () => {
    props.runSequence({
      sequence: "CourseList",
      action: ACTIONS.TOGGLE_ACTIVE,
    });
  };

  const handleConfirmSave = (e: SyntheticEvent, data: ButtonProps) => {
    props.runSequence({
      sequence: "CourseList",
      action: ACTIONS.SAVE,
      canceled: modConfirm?.canceled,
      isByPass: modConfirmChange?.isByPass,
      type: data.name as "NO_SEND" | "SEND",
      onConfirm: (type) => {
        setModConfirmChange({ isByPass: true, type });
      },
      onSuccess: handleCloseModConfirm,
    });
  };

  const handleDelete = () => {
    setModConfirm({ canceled: true });
  };

  const handleCloseDetail = () => {
    props.setProp("CourseListSequence.detail", null);
  };

  const handlePrintReport = () => {
    props.runSequence({ sequence: "CourseList", action: ACTIONS.PRINT_REPORT });
  };

  return (
    <div style={{ height: "calc(100vh - 7rem)", overflowY: "auto", ...props.style }}>
      <SnackMessage
        error={props.errorMessage?.[SEQUENCE_NAME]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
        }}
      />

      <CardCourseListUX
        detail={props.CourseListSequence?.detail}
        enabledExpiryDate={enabledExpiryDate}
        enabledParticipant={enabledParticipant}
        filter={props.CourseListSequence?.filter}
        hideDeleteCourse={props.hideDeleteCourse}
        hideNewCourse={props.hideNewCourse}
        link={link}
        readOnly={props.readOnly}
        showRequiredField={showRequiredField}
        tableData={formattedItems}
        totalParticipant={filteredParticipants.length}
        languageUX={props.languageUX}
        selectedEmployee={selectedEmployee}
        divisionOptions={props.divisionOptions}
        typeOptions={COURSE_TYPE_OPTIONS}
        getTrProps={handleGetTrProps}
        onAddEmployee={handleAddEmployee}
        onAddEvaluation={handleAddEvaluation}
        onAddLink={handleAddLink}
        onChangeFilter={handleChangeFilter}
        onChangeLink={handleChangeLink}
        onChangeValue={handleChangeValue}
        onClickCancel={handleCloseDetail}
        onClickNewCourse={handleClickNewCourse}
        onDelete={handleDelete}
        onSave={handleSave}
        attachmentList={
          <>
            {filteredAttachments.length > 0 && (
              <FormGroup style={{ marginBottom: "0.15rem", marginTop: "-0.25rem" }}>
                <FormField style={{ width: "130px" }}>
                  <label style={{ fontWeight: "normal" }}>Preview :</label>
                </FormField>
                <FormField width={6}>
                  <label style={{ fontWeight: "normal" }}>Title :</label>
                </FormField>
              </FormGroup>
            )}
            {filteredAttachments.map((item) => (
              <SubLinkAttachment
                key={`attachment-${item.index}`}
                readOnly={props.readOnly}
                url={item.link}
                onDeleteLink={handleDeleteLink(item.index)}
              />
            ))}
          </>
        }
        buttonActive={
          <ButtonLoadCheck
            setProp={props.setProp}
            style={{ minWidth: "max-content" }}
            color={detail?.is_active ? "orange" : "green"}
            paramKey={BTN_ACTS.TOGGLE_ACTIVE}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.TOGGLE_ACTIVE]}
            onClick={handleToggleActive}
            title={
              detail?.is_active
                ? intl.formatMessage({ id: "ปิดใช้งาน" })
                : intl.formatMessage({ id: "เปิดใช้งาน" })
            }
          />
        }
        buttonAdd={
          <ButtonLoadCheck
            setProp={props.setProp}
            style={{ minWidth: "max-content" }}
            color="green"
            paramKey={BTN_ACTS.ADD_DIVISION}
            title={intl.formatMessage({ id: "common.add" })}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.ADD_DIVISION]}
            onClick={handleAddEmployee}
            disabled={
              props.CourseListSequence?.detail?.participant === "department"
                ? !props.CourseListSequence.detail.division
                : !selectedEmployee
            }
          />
        }
        buttonPrint={
          <ButtonLoadCheck
            setProp={props.setProp}
            style={{ minWidth: "max-content" }}
            color="yellow"
            icon="print"
            paramKey={BTN_ACTS.PRINT_REPORT}
            title="Print Report"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.PRINT_REPORT]}
            onClick={handlePrintReport}
          />
        }
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="blue"
            paramKey={BTN_ACTS.SEARCH}
            title="SEARCH"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
            onClick={handleSearch}
          />
        }
        employeeSearchBox={
          <SearchBoxWithKey
            onEvent={props.onEvent}
            id="CLE"
            placeholder="Search Employee"
            selectedItem={selectedEmployee?.id || null}
            type="UserEmployee"
            inline
            searchedItemListWithKey={props.searchedItemListWithKey}
            setSelected={(value, key, data) => {
              setSelectedEmployee(data);
            }}
            disabled={
              !enabledParticipant || props.CourseListSequence?.detail?.participant !== "employee"
            }
          />
        }
        endTime={
          <TimeComboBox
            style={{ width: "100%" }}
            defaultValue={props.CourseListSequence?.detail?.end_time}
            disabled={props.readOnly}
            onTextChange={(value) => {
              handleChangeValue(null, { name: "end_time", value });
            }}
          />
        }
        evaluationList={
          <>
            {filteredEvaluations.length > 0 && (
              <FormGroup style={{ marginBottom: "0.15rem", marginTop: "-0.25rem" }}>
                <FormField width={4}>
                  <label style={{ fontWeight: "normal" }}>Subject :</label>
                </FormField>
                <FormField width={4}>
                  <label style={{ fontWeight: "normal" }}>Link :</label>
                </FormField>
              </FormGroup>
            )}
            {filteredEvaluations.map((item) => (
              <FormGroup key={`evaluation-${item.index}`} inline>
                <FormField width={4} inline>
                  <Input
                    name="subject"
                    value={item.subject}
                    fluid
                    onChange={handleChangeEvaluation(item.index)}
                  />
                </FormField>
                <FormField width={4} inline>
                  <Input
                    name="link"
                    value={item.link}
                    fluid
                    onChange={handleChangeEvaluation(item.index)}
                  />
                </FormField>
                <FormField inline>
                  {!props.readOnly && (
                    <Button
                      style={{ fontSize: "0.55rem" }}
                      color="red"
                      icon="minus"
                      size="mini"
                      onClick={handleDeleteEvaluation(item.index)}
                    />
                  )}
                </FormField>
              </FormGroup>
            ))}
          </>
        }
        participantList={
          <>
            {filteredParticipants.length > 0 && (
              <FormGroup style={{ marginBottom: "0.15rem", marginTop: "-0.25rem" }}>
                <FormField width={3}>
                  <label style={{ fontWeight: "normal" }}>Employee name :</label>
                </FormField>
                <FormField width={4}>
                  <label style={{ fontWeight: "normal" }}>Email :</label>
                </FormField>
              </FormGroup>
            )}

            {filteredParticipants.map((item) => {
              // ทำการลบ Participant ที่ตัวเองไม่ได้เป็นคนเพิ่มไม่ได้ (ยกเว้น Role HR)
              const enabledDelete =
                isRoleHR || !item.create_user ? true : item.create_user === props.django?.user?.id;

              return (
                <FormGroup key={`employee-${item.index}`} inline>
                  <FormField width={3} inline>
                    <label style={{ fontWeight: "normal" }}>{item.employee_name}</label>
                  </FormField>
                  <FormField width={4} inline>
                    <Input
                      name="email"
                      value={item.email}
                      fluid
                      onChange={handleChangeEmployee(item.index)}
                    />
                  </FormField>
                  <FormField inline>
                    <Button
                      style={{ fontSize: "0.55rem" }}
                      color="red"
                      disabled={!enabledDelete}
                      icon="minus"
                      size="mini"
                      onClick={handleDeleteEmployee(item.index)}
                    />
                  </FormField>
                </FormGroup>
              );
            })}
          </>
        }
        startTime={
          <TimeComboBox
            style={{ width: "100%" }}
            defaultValue={props.CourseListSequence?.detail?.start_time}
            disabled={props.readOnly}
            onTextChange={(value) => {
              handleChangeValue(null, { name: "start_time", value });
            }}
          />
        }
        statusFilter={
          <DropdownOptions
            style={{ width: "100%" }}
            name="status"
            value={props.CourseListSequence?.filter?.status ?? ""}
            fluid
            inline
            multiple
            search
            options={COURSE_STATUS_OPTIONS}
            onChange={handleChangeFilter}
          />
        }
      />

      <ModInfo
        style={{ width: "37rem" }}
        backgroundColor="var(--primary-theme-color)"
        buttonStyle={{ display: "none" }}
        open={!!modConfirm}
        titleName="Alert message"
        onClose={handleCloseModConfirm}
      >
        <div style={{ fontWeight: "bold", margin: "2.5rem 0" }}>{modConfirmTitle}</div>
        <div style={{ display: "flex", marginBottom: "0.5rem" }}>
          <ButtonLoadCheck
            setProp={props.setProp}
            style={{ flex: 1 }}
            color="green"
            name="SEND"
            paramKey={`${BTN_ACTS.SAVE}_SEND`}
            title="Save & Send"
            basic
            buttonLoadCheck={props.buttonLoadCheck?.[`${BTN_ACTS.SAVE}_SEND`]}
            onClick={handleConfirmSave}
          />
          <ButtonLoadCheck
            setProp={props.setProp}
            style={{ flex: 1, margin: "0 1rem" }}
            color="blue"
            name="NO_SEND"
            paramKey={`${BTN_ACTS.SAVE}_NO_SEND`}
            title="Save & Don't Send"
            basic
            buttonLoadCheck={props.buttonLoadCheck?.[`${BTN_ACTS.SAVE}_NO_SEND`]}
            onClick={handleConfirmSave}
          />
          <Button style={{ flex: 1 }} color="red" basic onClick={handleCloseModConfirm}>
            Back to Editing
          </Button>
        </div>
      </ModInfo>

      <ModConfirmCNMI
        approveButtonName={modConfirmChange?.type}
        approveButtonText="Confirm"
        denyButtonText="Cancel"
        modalStyle={{ minWidth: "32rem" }}
        openModal={!!modConfirmChange}
        titleColor="red"
        titleName="Confirm Data Change"
        onApprove={handleConfirmSave}
        onCloseWithDimmerClick={handleCloseModConfirm}
        onDeny={handleCloseModConfirm}
        approveButtonLoading={
          props.buttonLoadCheck?.[`${BTN_ACTS.SAVE}_${modConfirmChange?.type}_CHANGE`]
        }
        content={
          <div
            style={{
              lineHeight: 2,
              margin: "-0.5rem 0 -1.5rem",
              textAlign: "center",
            }}
          >
            <div style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
              <img
                style={{
                  marginBottom: "-2px",
                  marginRight: "0.5rem",
                  width: "17px",
                }}
                alt="caution-sign"
                src={IMAGES.cautionSign}
              />
              <span>At least one participation approval has been found.</span>
            </div>
            <div>
              Changing the training date{" "}
              <span style={{ fontWeight: "bold", textDecorationLine: "underline" }}>
                will clear all approval data
              </span>{" "}
              and{" "}
              <span style={{ fontWeight: "bold", textDecorationLine: "underline" }}>
                update the training schedule
              </span>{" "}
              on the Training Management screen for bot department managers and employees.
            </div>
          </div>
        }
      />
    </div>
  );
};

CardCourseList.displayName = "CardCourseList";

export default React.memo(CardCourseList);
